import React from "react";
import { Popover } from "antd";

import type { StoreOption } from "../hooks/useStoresOptions";
import type { AdminProfile } from "../layouts/AdminScopeContext";
import ManagerStoreSwitch from "./ManagerStoreSwitch";
import StoreSelect from "./StoreSelect";

type Props = {
  profile: AdminProfile | null;
  ready: boolean;
  collapsed: boolean;
  canSwitchViewStore: boolean;
  globalScope: boolean;
  activeStoreId: number | null;
  storeOptions: StoreOption[];
  onViewStoreChange: (storeId: number | null) => void;
};

function initialOf(label: string): string {
  const value = label.trim();
  if (!value || value.startsWith("总览")) return "总";
  return Array.from(value)[0] ?? "店";
}

const StoreScopeSlot: React.FC<Props> = ({
  profile,
  ready,
  collapsed,
  canSwitchViewStore,
  globalScope,
  activeStoreId,
  storeOptions,
  onViewStoreChange,
}) => {
  if (!ready || !profile) return <div className="admin-store-scope admin-store-scope--loading" />;

  const managerCanSwitch =
    !profile.isSuperAdmin && profile.role === "store_manager" && profile.managedStores.length > 1;
  const activeOption = storeOptions.find((option) => option.value === activeStoreId);
  const viewLabel = activeStoreId === null ? "总览（全部门店）" : activeOption?.label ?? profile.storeName;
  const managerLabel =
    profile.managedStores.find((store) => store.storeId === profile.storeId)?.storeNameCn ??
    profile.storeName ??
    "当前门店";
  const scopeLabel = canSwitchViewStore || managerCanSwitch ? "查看门店" : "当前门店";
  const currentLabel = canSwitchViewStore ? viewLabel : managerLabel;

  const fullControl = (
    <div className="admin-store-scope__body" data-testid="current-store-scope">
      <span className="admin-store-scope__label">{scopeLabel}</span>
      {managerCanSwitch ? (
        <ManagerStoreSwitch profile={profile} />
      ) : canSwitchViewStore ? (
        <StoreSelect
          value={activeStoreId ?? undefined}
          allowClear={globalScope}
          placeholder={globalScope ? "总览（全部门店）" : "选择名下门店"}
          onChange={(value) => onViewStoreChange(typeof value === "number" ? value : null)}
          popupMatchSelectWidth={false}
          data-testid="global-store-switch"
        />
      ) : (
        <div className="admin-store-scope__readonly" title={currentLabel}>
          {currentLabel}
        </div>
      )}
    </div>
  );

  if (!collapsed) return <aside className="admin-store-scope">{fullControl}</aside>;

  return (
    <aside className="admin-store-scope admin-store-scope--collapsed">
      <Popover placement="rightTop" trigger="click" content={fullControl}>
        <button
          type="button"
          className="admin-store-scope__initial"
          aria-label={`${scopeLabel}：${currentLabel}`}
          data-testid="current-store-scope-collapsed"
        >
          {initialOf(currentLabel)}
        </button>
      </Popover>
    </aside>
  );
};

export default StoreScopeSlot;
